import { i18n } from "@lingui/core";

/**
 * Formats a wallet address to show first 6 and last 4 characters
 * @param address - The wallet address to format
 * @returns Formatted address string (e.g., "0x1234...abcd")
 */
export function formatAddress(address: string | undefined): string {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Formats a USD currency value with proper comma separators and 2 decimal places
 * @param amount - The USD amount as a number
 * @returns Formatted USD string with comma separators and exactly 2 decimal places
 */
export function formatCurrency(amount: number | undefined): string {
  if (amount === undefined || Number.isNaN(amount)) return "0.00";
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Extracts numeric value from formatted currency string
 * @param formattedValue - The formatted string (e.g., "$1,234.56", "1.2345 ETH")
 * @returns Numeric value or 0 if parsing fails
 */
export function extractNumericValue(formattedValue: string | undefined): number {
  if (!formattedValue || typeof formattedValue !== "string") return 0;

  // Remove common currency symbols and text, keep only numbers, decimals, and negative signs
  const cleaned = formattedValue.replace(/[$,\s%+]/g, "").replace(/[A-Za-z]+/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Determines the appropriate decimal places for tiered precision formatting
 * Used for NumberFlow and other components that need Intl.NumberFormatOptions
 * @param amount - The numeric amount
 * @returns Object with minimumFractionDigits and maximumFractionDigits
 */
export function getTieredPrecisionFormat(amount: number): {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
} {
  const absAmount = Math.abs(amount);

  if (absAmount >= 1) {
    return { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  }
  if (absAmount >= 0.01) {
    return { minimumFractionDigits: 0, maximumFractionDigits: 4 };
  }
  if (absAmount >= 0.0001) {
    return { minimumFractionDigits: 0, maximumFractionDigits: 6 };
  }
  // For values < 0.0001, use 6 decimals but NumberFlow will handle display
  return { minimumFractionDigits: 0, maximumFractionDigits: 6 };
}

/**
 * Extracts USD value from formatted string
 * @param usdString - The USD formatted string (e.g., "$1,234.56")
 * @returns Numeric value or 0 if parsing fails
 */
export function extractUsdValue(usdString: string | undefined): number {
  if (!usdString || typeof usdString !== "string") return 0;

  // Remove $ symbol and commas
  const cleaned = usdString.replace(/[$,]/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Normalize chain ID by removing CAIP-2 prefix if present
 * @param chainId - The chain ID to normalize (e.g., "eip155:1" or "1")
 * @returns Normalized chain ID without prefix (e.g., "1")
 */
export function normalizeChainId(chainId: string): string {
  return chainId.replace(/^eip155:/, "");
}

/**
 * Formats a swap rate: amountOut per 1 unit of amountIn.
 * Uses token-style formatting with tiered precision.
 *
 * Example: amountIn=2, amountOut=3, dstSymbol="USDC" → rate=1.5 → "1.5 USDC"
 */
export function formatBalanceRate(
  amountIn: string | number | undefined,
  amountOut: string | number | undefined,
  dstTokenSymbol: string,
): string {
  const src =
    typeof amountIn === "number"
      ? amountIn
      : extractNumericValue(typeof amountIn === "string" ? amountIn : "");
  const dst =
    typeof amountOut === "number"
      ? amountOut
      : extractNumericValue(typeof amountOut === "string" ? amountOut : "");

  if (!Number.isFinite(src) || src <= 0) return `0 ${dstTokenSymbol}`;
  const rate = dst / src;
  if (!Number.isFinite(rate) || rate <= 0) return `0 ${dstTokenSymbol}`;

  return formatTokenBalance(rate, dstTokenSymbol);
}

/**
 * Formats large numbers with abbreviated suffixes (localized: M for millions, B for billions, T for trillions).
 * Only applies abbreviations for numbers >= 1,000,000.
 * Numbers below 1 million are formatted with standard token balance formatting.
 * Whole numbers don't show ".0" (e.g., "2M" instead of "2.0M"), but decimals are preserved (e.g., "2.5M").
 * Uses Intl.NumberFormat for proper localization support.
 *
 * Examples (en-US):
 * - 999,999 → "999,999.000000" (no abbreviation)
 * - 1,000,000 → "1M"
 * - 1,500,000 → "1.5M"
 * - 2,000,000 → "2M" (no .0)
 * - 1,000,000,000 → "1B"
 * - 1,000,000,000,000 → "1T"
 *
 * @param amount - The numeric amount to format
 * @param tokenDecimals - Number of decimal places for the token (used for numbers < 1M)
 * @param tokenSymbol - Token symbol to append (e.g., "USDT")
 * @param locale - Optional locale string (defaults to current i18n locale or "en-US")
 * @returns Formatted string with abbreviation and token symbol
 */
export function formatLargeNumber(
  amount: number,
  _tokenDecimals: number, // Kept for backward compatibility but no longer used
  tokenSymbol: string,
  locale?: string,
): string {
  if (!Number.isFinite(amount) || amount < 0) {
    return formatTokenBalance(amount, tokenSymbol);
  }

  // Only abbreviate numbers >= 1 million
  if (amount < 1_000_000) {
    return formatTokenBalance(amount, tokenSymbol);
  }

  // Use Intl.NumberFormat for proper localization support
  // Get locale from parameter, i18n, or fallback to en-US
  const activeLocale = locale || (typeof i18n !== "undefined" ? i18n.locale : undefined) || "en-US";

  // Format with compact notation (automatically handles localization)
  const formatter = new Intl.NumberFormat(activeLocale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });

  let abbreviated = formatter.format(amount);

  // Remove trailing ".0" but keep decimals when non-zero (e.g., "2.0M" -> "2M", but "2.5M" stays "2.5M")
  // This regex handles various locale-specific formats including:
  // - English: "2.0M" -> "2M"
  // - Korean: "2.0만" -> "2만" (만 = 10,000), "2.0백만" -> "2백만" (백만 = million)
  // - Other locales with different decimal separators
  // Match: decimal point + zero + optional space/non-breaking space + abbreviation character(s)
  abbreviated = abbreviated.replace(/[.,]\s*0\s*([\s\u00A0]?[A-Za-z가-힣\u4e00-\u9fff]+)/u, "$1");

  return `${abbreviated} ${tokenSymbol}`;
}

/**
 * Formats raw pool amount to full display format (without abbreviation) for tooltips
 * @param rawAmount - Raw amount string in smallest units (e.g., wei)
 * @param tokenDecimals - Number of decimal places for the token
 * @param tokenSymbol - Token symbol for display
 * @returns Formatted string like "1,234.567890 USDT"
 */
export function formatFullPoolAmount(
  rawAmount: string | undefined,
  tokenDecimals: number,
  tokenSymbol: string,
): string {
  if (!rawAmount) return "N/A";
  const decimals = Number.isFinite(tokenDecimals) && tokenDecimals >= 0 ? tokenDecimals : 6;
  const amount = Number(rawAmount) / 10 ** decimals;
  return formatTokenBalance(amount, tokenSymbol, { forTooltip: true });
}

/**
 * Formats a token balance with tiered precision for optimal readability.
 *
 * Display rules:
 * - ≥1 → 2 decimals (e.g., "1.23 ETH")
 * - 0.01–1 → 4 decimals (e.g., "0.2567 ETH")
 * - 0.0001–0.01 → 6 decimals (e.g., "0.002345 ETH")
 * - <0.0001 → render "< 0.0001 ETH" (or swap symbol per asset)
 *
 * Features:
 * - Collapses trailing zeros after rounding
 * - Supports tooltip mode for full precision display
 * - Locale-aware thousand separators
 * - Handles edge cases gracefully
 *
 * @param amount - The token balance amount (number or string)
 * @param tokenSymbol - The token symbol to append (e.g., "ETH", "BTC")
 * @param options - Optional configuration
 * @param options.forTooltip - If true, returns full precision value for tooltip display
 * @returns Formatted balance string with appropriate precision
 *
 * @example
 * formatTokenBalance(1.234567, "ETH") // "1.23 ETH"
 * formatTokenBalance(0.256789, "ETH") // "0.2568 ETH"
 * formatTokenBalance(0.00234567, "ETH") // "0.002346 ETH"
 * formatTokenBalance(0.000008912345, "ETH") // "< 0.0001 ETH"
 * formatTokenBalance(0.000008912345, "ETH", { forTooltip: true }) // "0.000008912345 ETH"
 */
export function formatTokenBalance(
  amount: string | number | undefined,
  tokenSymbol: string,
  options?: { forTooltip?: boolean },
): string {
  // Handle undefined/null/empty input
  if (amount === undefined || amount === null || amount === "") {
    return `0 ${tokenSymbol}`;
  }

  // Convert to number and validate
  const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount;

  if (Number.isNaN(numAmount) || !Number.isFinite(numAmount)) {
    return `0 ${tokenSymbol}`;
  }

  // Handle zero
  if (numAmount === 0) {
    return `0 ${tokenSymbol}`;
  }

  const absAmount = Math.abs(numAmount);
  const isNegative = numAmount < 0;
  const prefix = isNegative ? "-" : "";

  // Tooltip mode: return full precision with all significant digits
  if (options?.forTooltip) {
    // Use enough precision to show all significant digits (up to 18 decimals)
    // Remove trailing zeros
    const formatted = absAmount.toLocaleString("en-US", {
      maximumFractionDigits: 18,
      minimumFractionDigits: 0,
    });
    return `${prefix}${formatted} ${tokenSymbol}`;
  }

  // Determine precision based on value tier
  let precision: number;
  let minFractionDigits: number;
  let showLessThan = false;

  if (absAmount >= 1) {
    precision = 2;
    minFractionDigits = 2; // Always show 2 decimals for values >= 1
  } else if (absAmount >= 0.01) {
    precision = 4;
    minFractionDigits = 0; // Collapse trailing zeros for values < 1
  } else if (absAmount >= 0.0001) {
    precision = 6;
    minFractionDigits = 0; // Collapse trailing zeros for values < 1
  } else {
    // < 0.0001: show "< 0.0001" format
    showLessThan = true;
    precision = 4; // For the threshold display
    minFractionDigits = 0;
  }

  // Format with the determined precision
  let formatted = absAmount.toLocaleString("en-US", {
    maximumFractionDigits: precision,
    minimumFractionDigits: minFractionDigits,
  });

  // Check if the formatted value rounds to >= 1 (e.g., 0.999999 -> 1)
  // If so, re-format with 2 decimals and always show trailing zeros
  const formattedNumeric = Number.parseFloat(formatted.replace(/,/g, ""));
  if (formattedNumeric >= 1 && absAmount < 1) {
    formatted = absAmount.toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  } else if (absAmount < 1) {
    // Collapse trailing zeros after the decimal point only for values < 1 that don't round to >= 1
    formatted = formatted.replace(/\.0+$/, "");
  }

  // Handle the "< 0.0001" case
  if (showLessThan) {
    // Format the threshold value (0.0001) with the symbol
    const threshold = (0.0001).toLocaleString("en-US", {
      maximumFractionDigits: 4,
      minimumFractionDigits: 0,
    });
    return `< ${threshold} ${tokenSymbol}`;
  }

  return `${prefix}${formatted} ${tokenSymbol}`;
}
